/**
 * Utilities for generating and parsing sequence IDs for shareable practice mode games
 */

/**
 * Generate a short hash ID from a date
 * Format: 6-character alphanumeric hash that encodes the date
 */
export function generateSequenceId(date: Date): string {
  // Create a simple hash from the date components
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Combine date components into a unique number
  const dateNumber = (year - 2000) * 10000 + month * 100 + day;
  
  // Convert to base36 for compact representation
  const base36 = dateNumber.toString(36);
  
  // Add a simple checksum to validate the hash
  const checksum = (year + month + day) % 36;
  const checksumChar = checksum.toString(36);
  
  return (base36 + checksumChar).toLowerCase();
}

/**
 * Parse a sequence ID back to a date
 * Returns null if the ID is invalid
 */
export function parseSequenceId(sequenceId: string): Date | null {
  try {
    if (!sequenceId || sequenceId.length < 4) {
      console.warn('Sequence ID validation failed: Invalid format or too short');
      return null;
    }
    
    // Extract the base36 part and checksum
    const base36Part = sequenceId.slice(0, -1);
    const checksumChar = sequenceId.slice(-1);
    
    // Convert back from base36
    const dateNumber = parseInt(base36Part, 36);
    
    if (isNaN(dateNumber)) {
      console.warn('Sequence ID validation failed: Invalid base36 encoding');
      return null;
    }
    
    // Extract date components
    const year = Math.floor(dateNumber / 10000) + 2000;
    const month = Math.floor((dateNumber % 10000) / 100);
    const day = dateNumber % 100;
    
    console.log('Parsed sequence components:', { sequenceId, year, month, day, dateNumber });
    
    // Validate checksum
    const expectedChecksum = (year + month + day) % 36;
    const expectedChecksumChar = expectedChecksum.toString(36);
    
    if (checksumChar !== expectedChecksumChar) {
      console.warn('Sequence ID validation failed: Checksum mismatch', {
        expected: expectedChecksumChar,
        actual: checksumChar
      });
      return null;
    }
    
    // Expanded year range to handle more sequences
    if (year < 2020 || year > 2040) {
      console.warn('Sequence ID validation failed: Year out of range', { year });
      return null;
    }
    
    // Validate month range
    if (month < 0 || month > 11) {
      console.warn('Sequence ID validation failed: Invalid month', { month });
      return null;
    }
    
    // Create and validate the date
    const parsedDate = new Date(year, month, day);
    
    // Basic validation - ensure the date components match what we expect
    if (parsedDate.getFullYear() !== year || 
        parsedDate.getMonth() !== month || 
        parsedDate.getDate() !== day) {
      console.warn('Sequence ID validation failed: Date reconstruction mismatch', {
        expected: { year, month, day },
        actual: { 
          year: parsedDate.getFullYear(), 
          month: parsedDate.getMonth(), 
          day: parsedDate.getDate() 
        }
      });
      return null;
    }
    
    console.log('Successfully parsed sequence ID:', { sequenceId, parsedDate });
    return parsedDate;
  } catch (error) {
    console.error('Error parsing sequence ID:', error);
    return null;
  }
}

/**
 * Validate that a sequence ID is properly formatted
 */
export function isValidSequenceId(sequenceId: string): boolean {
  return parseSequenceId(sequenceId) !== null;
}

/**
 * Get the current sequence ID from URL parameters
 */
export function getSequenceIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('sequence');
}